/**
 * L12 — Text Highlight Layout
 * Animated keyword-based text. Each word appears in sequence.
 * Good for: key facts, bullet points, emphasis.
 */
import React from 'react';
import { AbsoluteFill, useCurrentFrame } from 'remotion';
import type { SceneProps } from '../../types';
import { SceneContainer, GlobalOverlays, SubheadlineText } from './shared';

const AnimatedWord: React.FC<{
  word: string; delay: number; isHighlighted?: boolean;
  accent: string; fontSize: number;
}> = ({ word, delay, isHighlighted, accent, fontSize }) => {
  const frame = useCurrentFrame();
  const f = Math.max(0, frame - delay);
  const p = Math.min(1, f / 12);
  const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;

  return (
    <span style={{
      display: 'inline-block',
      opacity: eased,
      transform: `translateY(${(1 - eased) * 16}px)`,
      color: isHighlighted ? accent : '#fff',
      background: isHighlighted ? `${accent}20` : 'transparent',
      borderRadius: isHighlighted ? 4 : 0,
      padding: isHighlighted ? '2px 8px' : '0',
      fontWeight: isHighlighted ? 900 : 600,
      fontSize,
      marginRight: 12,
      marginBottom: 8,
      fontFamily: 'Roboto, sans-serif',
    }}>
      {word}
    </span>
  );
};

export const L12_TextHighlight: React.FC<SceneProps> = (props) => {
  const { headline, highlightWords = [], assets, colorPalette, headlineFont, subheadlineFont, subheadline } = props;
  const primary = assets.primary;

  const words = headline.split(/\s+/);
  const highlightSet = new Set(highlightWords.map((w) => w.toLowerCase()));
  const fontSize = Math.min(headlineFont.fontSize * 1.1, 58);

  return (
    <SceneContainer bg={colorPalette.primary}>
      {/* Background image — heavily darkened */}
      {primary && (
        <AbsoluteFill style={{ opacity: 0.15 }}>
          <img src={primary.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </AbsoluteFill>
      )}

      {/* Gradient */}
      <AbsoluteFill style={{
        background: `linear-gradient(135deg, ${colorPalette.primary} 0%, ${colorPalette.secondary}80 100%)`,
      }} />

      <AbsoluteFill style={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: '80px 120px 120px',
      }}>
        {/* Animated words */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline' }}>
          {words.map((word, i) => (
            <AnimatedWord
              key={i}
              word={word}
              delay={i * 4}
              isHighlighted={highlightSet.has(word.toLowerCase().replace(/[^a-zऀ-ॿ]/gi, ''))}
              accent={colorPalette.accent}
              fontSize={fontSize}
            />
          ))}
        </div>

        {subheadline && (
          <SubheadlineText
            text={subheadline}
            font={{ ...subheadlineFont, color: 'rgba(255,255,255,0.65)', fontSize: 24 }}
            delay={words.length * 4 + 8}
            style={{ marginTop: 24 }}
            layerKey="subheadline"
          />
        )}
      </AbsoluteFill>

      <GlobalOverlays props={props} />
    </SceneContainer>
  );
};
